import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useCallback, useEffect, useState } from "react";
import { ProduccionB2bShell } from "@/components/b2b/ProduccionB2bShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronDown, ChevronRight, Download, RefreshCw } from "lucide-react";
import { FilterBar, Panel, QuickActions } from "@/components/ds";
import type { QuickAction } from "@/components/ds";
import {
  exportInstitutionalOrdersCsv,
  formatSelectionLine,
  selectionRows,
} from "@/lib/institutional-order-utils";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { toast } from "sonner";

export const Route = createFileRoute("/app/produccion/pedidos")({
  component: ProduccionPedidosPage,
  head: () => ({ meta: [{ title: "Pedidos institucionales — B2B" }] }),
});

interface B2bChannelOption {
  id: string;
  name: string;
}

interface InstitutionalOrderAdmin {
  id: string;
  customerName: string;
  institutionName: string | null;
  requesterName: string | null;
  channelId: string | null;
  channelName: string | null;
  menuName: string | null;
  status: string;
  total: number;
  createdAt: string;
  notes: string | null;
  selections: Record<string, unknown>;
}

function ProduccionPedidosPage() {
  const tid = useWorkspaceTenantId();
  const [b2bChannels, setB2bChannels] = useState<B2bChannelOption[]>([]);
  const [orders, setOrders] = useState<InstitutionalOrderAdmin[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersFrom, setOrdersFrom] = useState(new Date().toISOString().slice(0, 10));
  const [ordersTo, setOrdersTo] = useState(new Date().toISOString().slice(0, 10));
  const [ordersStatus, setOrdersStatus] = useState("all");
  const [ordersChannel, setOrdersChannel] = useState("all");
  const [openOrderId, setOpenOrderId] = useState<string | null>(null);

  useEffect(() => {
    if (!tid) return;
    void apiGetJson<B2bChannelOption[]>("/api/b2b/channels", tid).then(setB2bChannels).catch(() => {});
  }, [tid]);

  const loadOrders = useCallback(async () => {
    if (!tid) return;
    setOrdersLoading(true);
    try {
      const params = new URLSearchParams();
      if (ordersFrom) params.set("from", ordersFrom);
      if (ordersTo) params.set("to", ordersTo);
      if (ordersStatus !== "all") params.set("status", ordersStatus);
      if (ordersChannel !== "all") params.set("channelId", ordersChannel);
      const list = await apiGetJson<InstitutionalOrderAdmin[]>(
        `/api/institutional/orders?${params.toString()}`,
        tid,
      );
      setOrders(list);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar pedidos");
    } finally {
      setOrdersLoading(false);
    }
  }, [tid, ordersFrom, ordersTo, ordersStatus, ordersChannel]);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  const exportOrdersCsv = () => {
    if (!orders.length) {
      toast.error("No hay pedidos para exportar");
      return;
    }
    exportInstitutionalOrdersCsv(orders);
    toast.success("CSV descargado");
  };

  const updateOrderStatus = async (orderId: string, status: string) => {
    if (!tid) return;
    try {
      await apiSendJson("/api/institutional/orders", "PATCH", { orderId, status }, tid);
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
      toast.success("Estado actualizado");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al actualizar");
    }
  };

  const pesos = (n: number) => n.toLocaleString("es-AR", { style: "currency", currency: "ARS" });

  const headerActions: QuickAction[] = [
    { key: "actualizar", label: "Actualizar", icon: RefreshCw, variant: "secondary", onClick: () => void loadOrders() },
    { key: "csv", label: "Exportar CSV", icon: Download, variant: "outline", disabled: !orders.length, onClick: exportOrdersCsv },
  ];

  return (
    <ProduccionB2bShell title="Pedidos institucionales">
      <Panel title="Pedidos institucionales" actions={<QuickActions actions={headerActions} />}>
        <div className="space-y-4">
        <FilterBar>
          <div>
            <Label>Desde</Label>
            <Input
              type="date"
              value={ordersFrom}
              onChange={(e) => setOrdersFrom(e.target.value)}
              className="mt-1 w-40"
            />
          </div>
          <div>
            <Label>Hasta</Label>
            <Input
              type="date"
              value={ordersTo}
              onChange={(e) => setOrdersTo(e.target.value)}
              className="mt-1 w-40"
            />
          </div>
          <div>
            <Label>Estado</Label>
            <Select value={ordersStatus} onValueChange={setOrdersStatus}>
              <SelectTrigger className="mt-1 w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="PENDIENTE">Pendiente</SelectItem>
                <SelectItem value="CONFIRMADO">Confirmado</SelectItem>
                <SelectItem value="EN_PREPARACION">En preparación</SelectItem>
                <SelectItem value="LISTO">Listo</SelectItem>
                <SelectItem value="ENTREGADO">Entregado</SelectItem>
                <SelectItem value="CANCELADO">Cancelado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Canal</Label>
            <Select value={ordersChannel} onValueChange={setOrdersChannel}>
              <SelectTrigger className="mt-1 w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {b2bChannels.map((ch) => (
                  <SelectItem key={ch.id} value={ch.id}>
                    {ch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </FilterBar>

        <div className="overflow-x-auto rounded-xl border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Solicitante</TableHead>
                <TableHead>Canal</TableHead>
                <TableHead>Detalle</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ordersLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                    Cargando pedidos…
                  </TableCell>
                </TableRow>
              ) : !orders.length ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                    Sin pedidos para los filtros seleccionados
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((o) => {
                  const expanded = openOrderId === o.id;
                  const items = selectionRows(o.selections);
                  return (
                    <Fragment key={o.id}>
                      <TableRow
                        className="cursor-pointer"
                        onClick={() => setOpenOrderId(expanded ? null : o.id)}
                      >
                        <TableCell className="text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1">
                            {expanded ? (
                              <ChevronDown className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5" />
                            )}
                            {new Date(o.createdAt).toLocaleString("es-AR")}
                          </span>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm font-medium">{o.requesterName ?? o.customerName}</p>
                          <p className="text-xs text-muted-foreground">{o.institutionName ?? "—"}</p>
                        </TableCell>
                        <TableCell className="text-sm">{o.channelName ?? "—"}</TableCell>
                        <TableCell className="text-sm">{o.menuName ?? "Pedido catálogo"}</TableCell>
                        <TableCell className="text-right font-medium">{pesos(o.total)}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Select value={o.status} onValueChange={(v) => void updateOrderStatus(o.id, v)}>
                            <SelectTrigger className="h-8 w-[170px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="PENDIENTE">Pendiente</SelectItem>
                              <SelectItem value="CONFIRMADO">Confirmado</SelectItem>
                              <SelectItem value="EN_PREPARACION">En preparación</SelectItem>
                              <SelectItem value="LISTO">Listo</SelectItem>
                              <SelectItem value="ENTREGADO">Entregado</SelectItem>
                              <SelectItem value="CANCELADO">Cancelado</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                      {expanded && (
                        <TableRow>
                          <TableCell colSpan={6} className="bg-muted/30 text-sm">
                            <div className="space-y-2 py-1">
                              {o.notes && (
                                <p className="text-muted-foreground">
                                  <span className="font-medium text-foreground">Notas:</span> {o.notes}
                                </p>
                              )}
                              {!items.length ? (
                                <p className="text-muted-foreground">Sin ítems detallados.</p>
                              ) : (
                                <ul className="space-y-1">
                                  {items.map((row, idx) => (
                                    <li key={idx}>{formatSelectionLine(row, pesos)}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        </div>
      </Panel>
    </ProduccionB2bShell>
  );
}
